import { useStacklineTheme } from '@stackline/ui';
import React, { useState, useCallback } from 'react';
import {
  BeaconProSidebarFilters,
  ICON_STYLES
} from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/Filters/constants';
import CheckboxItem from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/components/CheckboxItem';
import GenericSidebarFilter, {
  GenericSidebarFilterProps
} from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/components/GenericSidebarFilter';
import {
  FILTER_OPERATION,
  useSavedSearchBuilder
} from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/hooks/useSavedSearchBuilder';
import { SlSidebarScrollableContainer } from 'src/components/BeaconV2Components/SlSidebar';
import { SlExcludeSelectedCheckbox, SlSelectedCheckbox } from 'src/components/SvgIcons';
import { useAppSelector } from 'src/utils/Hooks';

const LegacyCategorySidebarFilter = (props: GenericSidebarFilterProps) => {
  const theme = useStacklineTheme();

  const { formData, updateSavedSearch } = useSavedSearchBuilder();
  const isExcluded = formData.termFilters?.excludedCategoryId?.values.length > 0;

  const selectedCategories = (
    formData.termFilters?.categoryId.values.length
      ? formData.termFilters?.categoryId.values
      : formData.termFilters?.excludedCategoryId.values.length
      ? formData.termFilters?.excludedCategoryId.values
      : []
  ).map((value) => ({ name: value.n, id: value.i }));

  const categoryOptions = useAppSelector((state) =>
    state.categories.map((category) => ({
      ...category,
      selected: selectedCategories.some((selectedCategory) => Number(selectedCategory.id) === Number(category.id))
    }))
  );

  /**
   * Search input for filtering down categories
   */
  const [currentInput, setCurrentInput] = useState<string>('');

  /**
   * Toggle mode for include/exclude; determines which icon to render in the renderItems function.
   */
  const [toggleMode, setToggleMode] = useState<'include' | 'exclude'>(isExcluded ? 'exclude' : 'include');

  /**
   * Handles the selection of a category.
   * Updates the selected state of the category and filters down the available subcategories based on the selected categories.
   */
  const handleItemSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;

      if (toggleMode === 'include') {
        updateSavedSearch({
          key: BeaconProSidebarFilters.Category.filterKey,
          newValue: value,
          operation: FILTER_OPERATION.Toggle
        });
      } else {
        updateSavedSearch({
          key: BeaconProSidebarFilters.ExcludedCategory.filterKey,
          newValue: value,
          operation: FILTER_OPERATION.Toggle
        });
      }
    },
    [toggleMode, updateSavedSearch]
  );

  const getIcon = useCallback(() => {
    switch (toggleMode) {
      case 'include': {
        return <SlSelectedCheckbox style={ICON_STYLES} />;
      }

      case 'exclude': {
        return <SlExcludeSelectedCheckbox style={ICON_STYLES} />;
      }
      default:
        return null;
    }
  }, [toggleMode]);

  /**
   * Renders the list of items as checkboxes.
   */
  const renderItems = useCallback(() => {
    return (
      <SlSidebarScrollableContainer>
        {categoryOptions
          ?.filter((category) => category?.name?.toLowerCase().includes(currentInput.toLowerCase()))
          .sort((a, b) => (a.selected === b.selected ? 0 : a.selected ? -1 : 1)) // sort by checked items
          .map((category, index) => (
            <CheckboxItem
              onClick={handleItemSelect}
              value={category.id}
              key={index}
              label={category.name}
              theme={theme}
              checked={category.selected}
              checkedIcon={getIcon()}
            />
          ))}
      </SlSidebarScrollableContainer>
    );
  }, [categoryOptions, currentInput, theme, handleItemSelect, getIcon]);

  /**
   * Handles the change of the input to filter down the categories.
   */
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentInput(e.target.value);
  };

  /**
   * Clears the selected categories and subcategories.
   */
  const handleClear = () => {
    updateSavedSearch({
      key: BeaconProSidebarFilters.Category.filterKey,
      callback: (form) => {
        form.termFilters[BeaconProSidebarFilters.Category.filterKey].values = [];
        form.termFilters[BeaconProSidebarFilters.ExcludedCategory.filterKey].values = [];
        return form;
      },
      operation: null,
      newValue: null
    });
    setCurrentInput('');
  };

  const handleToggle = (toggle: 'include' | 'exclude') => {
    setToggleMode(toggle);

    if (toggle === 'include') {
      updateSavedSearch({
        key: BeaconProSidebarFilters.Category.filterKey,
        callback: (form) => {
          form.termFilters[BeaconProSidebarFilters.Category.filterKey].values = selectedCategories.map((category) => ({
            i: category.id,
            n: category.name
          }));

          form.termFilters[BeaconProSidebarFilters.ExcludedCategory.filterKey].values = [];

          return form;
        },
        operation: null,
        newValue: null
      });
    } else {
      updateSavedSearch({
        key: BeaconProSidebarFilters.ExcludedCategory.filterKey,
        callback: (form) => {
          form.termFilters[BeaconProSidebarFilters.ExcludedCategory.filterKey].values = selectedCategories.map(
            (category) => ({
              i: category.id,
              n: category.name
            })
          );

          form.termFilters[BeaconProSidebarFilters.Category.filterKey].values = [];

          return form;
        },
        operation: null,
        newValue: null
      });
    }
  };

  return (
    <GenericSidebarFilter
      {...props}
      renderItems={renderItems}
      handleClear={handleClear}
      handleToggle={handleToggle}
      toggleMode={toggleMode}
      includeToggles
      includeClear={selectedCategories?.length > 0}
      sidebarInputProps={{
        ...props.sidebarInputProps,
        value: currentInput,
        onChange: handleChange
      }}
    />
  );
};
export default LegacyCategorySidebarFilter;
